import { motion } from 'framer-motion';
import { Calendar, Clock, TrendingUp, Tag } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogApi, BlogPost, BlogCategory } from '../services/api';

interface LatestBlogsSidebarProps {
  currentPostId?: string; // To exclude current post from latest posts
}

export default function LatestBlogsSidebar({ currentPostId }: LatestBlogsSidebarProps) {
  const [latestPosts, setLatestPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadSidebarData();
  }, []);

  const loadSidebarData = async () => {
    try {
      setIsLoading(true);

      const [postsResponse, categoriesResponse] = await Promise.all([
        blogApi.public.getPosts({ limit: 5 }),
        blogApi.categories.getCategories(),
      ]);

      if (postsResponse.success && postsResponse.data) {
        setLatestPosts(postsResponse.data.posts);
      }

      if (categoriesResponse.success && categoriesResponse.data) {
        setCategories(categoriesResponse.data);
      }
    } catch (error) {
      console.error('Error loading sidebar data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const visiblePosts = latestPosts.filter((post) => post.id !== currentPostId).slice(0, 4);

  if (isLoading) {
    return (
      <aside className="lg:w-80 lg:shrink-0 space-y-8">
        <div className="public-card public-card--white p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <div className="h-16 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside className="lg:w-80 lg:shrink-0 space-y-8">
      {/* Latest Posts */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        className="public-card public-card--white p-6"
      >
        <div className="flex items-center space-x-2 mb-6">
          <TrendingUp className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Keep exploring</h3>
        </div>

        <div className="space-y-6">
          {visiblePosts.map((post, index) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="group"
            >
              <Link to={`/blog/${post.slug}`} className="block">
                {/* Post Image */}
                {post.coverImage && (
                  <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden mb-3">
                    <img
                      src={post.coverImage}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}

                {/* Category */}
                {post.category && (
                  <div className="mb-2">
                    <span
                      className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium"
                      style={{
                        backgroundColor: post.category.color + '20',
                        color: post.category.color,
                      }}
                    >
                      {post.category.name}
                    </span>
                  </div>
                )}

                {/* Title */}
                <h4 className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors mb-2 line-clamp-2 leading-snug">
                  {post.title}
                </h4>

                {/* Meta Info */}
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-3 w-3" />
                    <span>{formatDate(post.publishedAt!)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock className="h-3 w-3" />
                    <span>{post.readingTimeMinutes} min</span>
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>

        {visiblePosts.length > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <Link
              to="/blog"
              className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
            >
              Explore all articles →
            </Link>
          </div>
        )}
      </motion.div>

      {/* Categories */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="public-card public-card--blue p-6"
      >
        <div className="flex items-center space-x-2 mb-6">
          <Tag className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Find your next idea</h3>
        </div>

        <div className="space-y-3">
          {categories.map((category, index) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Link
                to={`/blog?category=${category.slug}`}
                className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center space-x-3">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: category.color }}
                  />
                  <span className="font-medium text-gray-700 group-hover:text-gray-900">
                    {category.name}
                  </span>
                </div>
                <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {category.postCount || 0}
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Newsletter Signup or CTA */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="public-card public-card--purple p-6"
      >
        <h3 className="text-xl font-semibold text-gray-900 mb-3">Give your next idea a plan.</h3>
        <p className="text-gray-600 leading-relaxed mb-5">
          Connect your vision to clear priorities, accountable owners and the work that moves your
          company forward—all in one workspace.
        </p>
        <Link to="/sign-up" className="public-button public-button--primary w-full">
          Build my strategic plan
        </Link>
      </motion.div>
    </aside>
  );
}
